import { FC } from "react";
import dayjs from "$/utils/dayjs";
import ImageBackground from "@/components/ui/ImageBackground/ImageBackground.tsx";
import useStorage from "@/api/useStorage.ts";
import StorageBucket from "$/_const/StorageBucket.ts";
import { PropsWithClassName } from "@/types/PropsWithClassName";
import { cn } from "@/utils/classNames.ts";
import UnsplashCredit from "@/components/ui/UnsplashCredit/UnsplashCredit.tsx";

type TripHeaderProps = {
  contentClassName?: string;
  is_generating_image: boolean;
  description?: string | null;
  hasError: boolean;
  image_src?: string | null;
  image_alt?: string | null;
  image_photographer_name?: string | null;
  image_photographer_url?: string | null;
  image_source_url?: string | null;
  updated_at: string;
  name: string;
};

const TripHeader: FC<PropsWithClassName<TripHeaderProps>> = ({
  className,
  contentClassName,
  is_generating_image,
  description,
  hasError,
  image_src,
  image_alt,
  image_photographer_name,
  image_photographer_url,
  image_source_url,
  updated_at,
  name,
}) => {
  const { getPublicUrl } = useStorage(StorageBucket.TRIP_IMAGES);
  const isRemoteImage = image_src?.startsWith("https://") ||
    image_src?.startsWith("http://");
  const resolvedImageUrl = image_src
    ? isRemoteImage
      ? image_src
      : `${getPublicUrl(image_src)}?cache=${
        dayjs(updated_at).format(
          "YYYYMMDDHHmmss",
        )
      }`
    : undefined;

  return (
    <ImageBackground
      dark
      className={cn(
        "relative h-[360px] w-full md:h-[500px] lg:h-[560px]",
        className,
      )}
      isLoading={is_generating_image && !hasError}
      src={!is_generating_image && !hasError ? resolvedImageUrl : undefined}
    >
      <div
        className={cn(
          "flex h-full flex-col justify-end px-5 pb-8 pt-28 md:px-10 md:pb-10 lg:px-14",
          contentClassName,
        )}
      >
        <h1 className="font-feature text-5xl font-light text-white drop-shadow-lg md:text-6xl lg:text-7xl">
          {name}
        </h1>
        {description
          ? (
            <p className="mt-3 max-w-3xl font-feature text-lg font-medium italic leading-relaxed text-white/90 drop-shadow-md md:text-xl">
              {description}
            </p>
          )
          : null}
        {image_alt ? <span className="sr-only">{image_alt}</span> : null}
      </div>

      {image_photographer_name
        ? (
          <UnsplashCredit
            photographerName={image_photographer_name}
            photographerUrl={image_photographer_url}
            sourceUrl={image_source_url}
            className="absolute bottom-2 right-3 z-10 rounded bg-black/60 px-2 py-1 text-[11px] text-white"
          />
        )
        : null}
    </ImageBackground>
  );
};

export default TripHeader;
